"use client";

import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TransitionPanel } from "../../../../global-account/react/index.js";
import { cn } from "../../../../shared/utils/cn.js";
import { AnimatePresence, motion } from "motion/react";
import React, { createContext, useContext } from "react";
const TabsContext = createContext(undefined);
export function Tabs({
  value,
  onValueChange,
  children,
  className = ""
}) {
  return _jsx(TabsContext.Provider, {
    value: {
      selectedTab: value,
      onTabChange: onValueChange
    },
    children: _jsx("div", {
      className: className,
      children: children
    })
  });
}
export function TabsList({
  children,
  className = "",
  hideGradient = false
}) {
  return _jsxs("div", {
    className: "relative w-full",
    children: [_jsx("div", {
      role: "tablist",
      className: cn("no-scrollbar relative mb-4 flex items-center gap-2 whitespace-nowrap", className),
      children: children
    }), _jsx(AnimatePresence, {
      mode: "wait",
      children: !hideGradient && _jsxs(motion.div, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        },
        transition: {
          duration: 0.2,
          delay: 0.2
        },
        children: [_jsx("div", {
          className: "pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-8 bg-gradient-to-r from-[#15121c] to-transparent md:hidden"
        }), _jsx("div", {
          className: "pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-8 bg-gradient-to-l from-[#15121c] to-transparent md:hidden"
        })]
      }, "gradients-container")
    })]
  });
}
export const TabTrigger = React.forwardRef(({
  value,
  children,
  icon,
  disabled = false
}, ref) => {
  const context = useContext(TabsContext);
  if (!context) throw new Error("TabTrigger must be used within Tabs");
  const {
    selectedTab,
    onTabChange
  } = context;
  const isSelected = selectedTab === value;
  return _jsxs("button", {
    ref: ref,
    role: "tab",
    "aria-selected": isSelected,
    "aria-controls": `panel-${value}`,
    id: `tab-${value}`,
    onClick: () => onTabChange(value),
    className: `relative rounded-full px-4 py-2 text-sm text-white transition-all duration-200 hover:text-white focus:outline-none`,
    disabled: disabled,
    children: [_jsxs("span", {
      className: `${isSelected ? "opacity-100" : "opacity-50"} relative z-10 flex items-center gap-2 font-semibold uppercase`,
      children: [children, icon]
    }), isSelected && _jsx(motion.span, {
      layoutId: "activeTab",
      transition: {
        type: "spring",
        duration: 0.4
      },
      className: "from-as-light-brand to-as-brand/10 border-as-brand/30 absolute inset-0 z-0 rounded-full border border-t-white/15 bg-gradient-to-b shadow-lg"
    })]
  });
});
export function TabsContent(t0) {
  const $ = _c(4);
  const {
    value,
    children
  } = t0;
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("TabsContent must be used within Tabs");
  }
  const {
    selectedTab
  } = context;
  const isSelected = selectedTab === value;
  if (!isSelected) {
    return null;
  }
  const t1 = `panel-${value}`;
  const t2 = `tab-${value}`;
  let t3;
  if ($[0] !== children || $[1] !== t1 || $[2] !== t2) {
    t3 = _jsx("div", {
      role: "tabpanel",
      id: t1,
      "aria-labelledby": t2,
      tabIndex: 0,
      className: "focus:outline-none",
      children
    });
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export function TabsTransitionWrapper(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("TabsTransitionWrapper must be used within Tabs");
  }
  let t1;
  if ($[0] !== children || $[1] !== context) {
    const childrenArray = React.Children.toArray(children);
    let t2;
    if ($[3] !== context) {
      t2 = child => React.isValidElement(child) && "value" in child.props && child.props.value === context.selectedTab;
      $[3] = context;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    const activeIndex = childrenArray.findIndex(t2);
    t1 = _jsx(TransitionPanel, {
      activeIndex,
      className: "w-full",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      },
      variants: {
        enter: {
          opacity: 0,
          filter: "blur(10px)",
          x: 20
        },
        center: {
          opacity: 1,
          filter: "blur(0px)",
          x: 0
        },
        exit: {
          opacity: 0,
          filter: "blur(10px)",
          x: -20
        }
      },
      children: childrenArray
    });
    $[0] = children;
    $[1] = context;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}